/**
 * Changement du mot de passe, en bas de « Modifier mes informations » (route account/edit ; SPEC MC7).
 *
 * Retours de Daphné du 12/10 : « Modifier mon mot de passe » n'a pas besoin d'être un bouton aussi visible sur la
 * page principale de Mon compte (colonne « Mes informations ») : il passe dans la page Informations, ouverte par
 * « Modifier mes informations ». Carte « Mot de passe » à part, après les actions du formulaire des informations (un
 * formulaire ne peut pas en contenir un autre, et « Enregistrer mes informations » n'enregistre pas le mot de passe),
 * avec un bouton discret qui déplie le formulaire.
 *
 * Logique inchangée = UserProfile.tsx EditPassword() : POST auth/updatePassword, mêmes contrôles et messages ; masqué
 * en OAuth comme la v1 (lib/session.ts). Le serveur vérifie l'ancien mot de passe avec l'e-mail de la session
 * (AuthService::updatePassword, sans uid Firebase).
 */
import { useToast } from '@chakra-ui/react';
import { useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { canChangePassword } from '../lib/session';
import { Icon } from './Icon';

/** Carte « Mot de passe » : bouton « Modifier mon mot de passe » qui déplie le formulaire ; rien en OAuth. */
export function PasswordCard() {
  const [open, setOpen] = useState(false);
  const formId = useId();
  const titleId = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  // Formulaire replié (Annuler, enregistré) : le focus revient sur son bouton (il tombait sur BODY, revue du 04/10).
  const close = () => {
    setOpen(false);
    window.setTimeout(() => toggle.current?.focus(), 0);
  };
  if (!canChangePassword()) return null;

  return (
    <section className="mmv2-card mmv2-card--pad mmv2-acct-pwcard" aria-labelledby={titleId}>
      <h2 className="mmv2-acct-kicker" id={titleId}>
        Mot de passe
      </h2>
      <button
        ref={toggle}
        type="button"
        className="mmv2-btn mmv2-btn--ghost mmv2-btn--sm mmv2-acct-pwtoggle"
        aria-expanded={open}
        aria-controls={formId}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="lock" size={15} />
        Modifier mon mot de passe
      </button>
      {open ? <PasswordForm id={formId} onDone={close} /> : null}
    </section>
  );
}

interface EditPasswordFormData {
  previousPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

/** = UserProfile.tsx EditPassword() */
function PasswordForm({ id, onDone }: { id: string; onDone: () => void }) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<EditPasswordFormData>({ mode: 'onChange' });

  async function onSubmit(data: EditPasswordFormData) {
    const { confirmNewPassword, newPassword, previousPassword } = data;
    if (newPassword !== confirmNewPassword) {
      return setError('confirmNewPassword', { message: 'Le mot de passe ne correspond pas' });
    }
    try {
      await HTTP.post('/auth/updatePassword', { previousPassword, newPassword }).send();
      reset();
      toast({ title: 'Votre mot de passe a bien été modifié.', status: 'success' });
      onDone();
    } catch (e) {
      if (e instanceof ApiClientException) {
        if (e.ErrorCode === ApiClientExceptionErrorCode.InvalidLoginCredentials) {
          return setError('previousPassword', { message: 'Mot de passe invalide' });
        }
        // Erreur au format WordPress ({ code, message } : garde-fou du staging) : getMessage() vide, texte de repli
        // (revue r4 : alerte vide).
        setError('root', {
          message: e.getMessage() || 'Votre mot de passe n’a pas pu être modifié. Réessayez dans un instant.',
        });
        return true;
      }
      const { title, description } = resolveUserError('passwordChange', e);
      setError('root', { message: description ? `${title}. ${description}` : title });
    }
    return true;
  }

  const registerOptions = {
    minLength: { value: 6, message: 'Le mot de passe doit faire au moins 6 caractères' },
    required: 'Champ obligatoire',
  };

  const field = (name: keyof EditPasswordFormData, label: string, autoComplete: string, placeholder?: string) => {
    const error = errors[name]?.message;
    const inputId = `${id}-${name}`;
    return (
      <div className="mmv2-field mmv2-acct-field">
        <label className="mmv2-label" htmlFor={inputId}>
          {label}
        </label>
        <input
          id={inputId}
          className="mmv2-input"
          type="password"
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-err` : undefined}
          {...register(name, registerOptions)}
        />
        {error ? (
          <span id={`${inputId}-err`} className="mmv2-acct-error">
            {error}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <form id={id} className="mmv2-acct-form mmv2-acct-pwform" onSubmit={handleSubmit(onSubmit)}>
      {field('previousPassword', 'Mot de passe actuel', 'current-password')}
      {field('newPassword', 'Nouveau mot de passe', 'new-password', 'Au moins 6 caractères')}
      {field('confirmNewPassword', 'Confirmer le nouveau mot de passe', 'new-password')}
      {errors.root ? (
        <p className="mmv2-acct-error" role="alert">
          {errors.root.message}
        </p>
      ) : null}
      <div className="mmv2-acct-actions">
        <button type="submit" className="mmv2-btn mmv2-btn--dark mmv2-btn--sm" disabled={!isValid || isSubmitting} aria-busy={isSubmitting}>
          {isSubmitting ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          className="mmv2-btn mmv2-btn--quiet mmv2-btn--sm"
          onClick={() => {
            reset();
            onDone();
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
